import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Plus, Pencil, Trash2, Check, X, Loader2 } from "lucide-react";
import { useTalmidim } from "../context/TalmidimContext";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClassesManagement() {
  const { klasn, talmidim, refresh } = useTalmidim();
  const { toast } = useToast();

  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // {klas, studentCount}
  const [reassignTo, setReassignTo] = useState("");
  const [deleting, setDeleting] = useState(false);

  const studentCountFor = (klasId) =>
    talmidim.filter((t) => t.klasId === klasId).length;

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await axios.post(`${API}/klasn`, { name });
      await refresh();
      setNewName("");
      toast({ title: "Class added", description: name });
    } catch (e) {
      toast({
        title: "Error",
        description: e?.response?.data?.detail || "Failed to add class",
        variant: "destructive",
      });
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (klas) => {
    setEditingId(klas.id);
    setEditingName(klas.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveEdit = async (klas) => {
    const name = editingName.trim();
    if (!name || name === klas.name) {
      cancelEdit();
      return;
    }
    setSavingId(klas.id);
    try {
      await axios.patch(`${API}/klasn/${klas.id}`, { name });
      await refresh();
      toast({ title: "Class renamed", description: `${klas.name} → ${name}` });
      cancelEdit();
    } catch (e) {
      toast({
        title: "Error",
        description: e?.response?.data?.detail || "Rename failed",
        variant: "destructive",
      });
    } finally {
      setSavingId(null);
    }
  };

  const askDelete = (klas) => {
    const count = studentCountFor(klas.id);
    setConfirmDelete({ klas, studentCount: count });
    setReassignTo("");
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { klas, studentCount } = confirmDelete;
    if (studentCount > 0 && !reassignTo) {
      toast({
        title: "Choose a class",
        description: "Pick a class to move students to before deleting",
        variant: "destructive",
      });
      return;
    }
    setDeleting(true);
    try {
      const url =
        studentCount > 0
          ? `${API}/klasn/${klas.id}?reassignTo=${reassignTo}`
          : `${API}/klasn/${klas.id}`;
      await axios.delete(url);
      await refresh();
      toast({
        title: "Class deleted",
        description:
          studentCount > 0
            ? `${studentCount} student(s) reassigned`
            : "Class removed",
      });
      setConfirmDelete(null);
    } catch (e) {
      toast({
        title: "Error",
        description: e?.response?.data?.detail || "Delete failed",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card data-testid="classes-management-card">
      <CardHeader>
        <CardTitle>Classes</CardTitle>
        <CardDescription>
          Add, rename, or delete classes. When deleting a class with students,
          you'll be asked where to move them.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add new */}
        <div className="flex gap-2">
          <Input
            data-testid="new-class-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Add new class (e.g. כתה ז)"
            dir="rtl"
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAdd())}
          />
          <Button
            data-testid="add-class-btn"
            onClick={handleAdd}
            disabled={adding || !newName.trim()}
            variant="outline"
          >
            {adding ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-1" />
            )}
            Add
          </Button>
        </div>

        {/* List */}
        <div className="space-y-2">
          {klasn.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm">
              No classes yet. Add one above.
            </div>
          )}
          {klasn.map((k) => {
            const count = studentCountFor(k.id);
            const isEditing = editingId === k.id;
            return (
              <div
                key={k.id}
                data-testid={`class-row-${k.id}`}
                className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
              >
                {isEditing ? (
                  <>
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      dir="rtl"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); saveEdit(k); }
                        if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                      }}
                      className="flex-1"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => saveEdit(k)}
                      disabled={savingId === k.id}
                      title="Save"
                    >
                      {savingId === k.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4 text-emerald-600" />
                      )}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={cancelEdit} title="Cancel">
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex-1" dir="rtl">
                      <span className="text-slate-800 font-medium">{k.name}</span>
                      <span className="text-xs text-slate-500 ms-3">
                        {count} {count === 1 ? "student" : "students"}
                      </span>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => startEdit(k)}
                      title="Rename"
                      data-testid={`edit-class-${k.id}`}
                    >
                      <Pencil className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => askDelete(k)}
                      title="Delete"
                      data-testid={`delete-class-${k.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>

      {/* Delete confirm */}
      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete class{" "}
              <span dir="rtl" className="font-serif-heb">
                {confirmDelete?.klas.name}
              </span>
              ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.studentCount > 0 ? (
                <>
                  This class has{" "}
                  <strong>{confirmDelete.studentCount}</strong> student(s).
                  Choose another class to move them to. Their past late passes
                  will also be updated to reflect the new class.
                </>
              ) : (
                "This class has no students. It will be permanently removed."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {confirmDelete?.studentCount > 0 && (
            <div className="py-2">
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Reassign students to:
              </label>
              <Select value={reassignTo} onValueChange={setReassignTo}>
                <SelectTrigger data-testid="reassign-select">
                  <SelectValue placeholder="Select a class..." />
                </SelectTrigger>
                <SelectContent>
                  {klasn
                    .filter((k) => k.id !== confirmDelete.klas.id)
                    .map((k) => (
                      <SelectItem key={k.id} value={String(k.id)}>
                        <span dir="rtl">{k.name}</span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              disabled={deleting}
              data-testid="confirm-delete-class-btn"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete Class
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
